import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import Footer from "../components/Footer";

const STATUS_CONFIG = {
  pending: {
    label:   "Pending",
    badge:   "#92400E",
    badgeBg: "#FEF3C7",
    heading: "Your quote request is in our queue.",
    body:    "We have received your request and it is waiting to be reviewed by one of our licensed agents. We appreciate your patience.",
  },
  reviewed: {
    label:   "Under Review",
    badge:   "#1D4ED8",
    badgeBg: "#DBEAFE",
    heading: "Your quote is being reviewed.",
    body:    "Great news — our team is actively reviewing your request and comparing options across our carrier network to find the best coverage for you.",
  },
  quoted: {
    label:   "Quote Ready",
    badge:   "#065F46",
    badgeBg: "#D1FAE5",
    heading: "Your quote is ready!",
    body:    "We have found coverage options that match your needs. One of our licensed agents will be contacting you shortly to walk you through your personalized quote.",
  },
  closed: {
    label:   "Closed",
    badge:   "#6B7280",
    badgeBg: "#F3F4F6",
    heading: "This quote request has been closed.",
    body:    "This request has been closed by our team. If you believe this was done in error or would like to submit a new request, please contact us or request a new quote.",
  },
};

function formatDate(str) {
  return new Date(str).toLocaleDateString("en-US", {
    month: "long", day: "numeric", year: "numeric",
  });
}

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function ArrowRight() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Result Card ──────────────────────────────────────────────────────────────

function ResultCard({ quote, onReset }) {
  const cfg = STATUS_CONFIG[quote.status] || STATUS_CONFIG.pending;
  const types = (quote.selectedInsurance || []).map(capitalize).join(", ") || "—";

  return (
    <div
      className="w-full max-w-lg mx-auto rounded-2xl overflow-hidden"
      style={{ border: "1.5px solid rgba(1,46,114,0.12)", boxShadow: "0 8px 40px rgba(1,46,114,0.10)" }}
    >
      {/* Top bar */}
      <div style={{ background: "#012E72", padding: "24px 32px" }}>
        <p className="font-cinzel uppercase text-white/50 mb-1" style={{ fontSize: "9px", letterSpacing: "0.25em" }}>
          Quote Status
        </p>
        <p className="font-cinzel font-bold text-white" style={{ fontSize: "18px", letterSpacing: "0.04em" }}>
          {quote.firstName} {quote.lastName}
        </p>
      </div>

      {/* Body */}
      <div className="bg-white px-8 py-7 flex flex-col gap-5">

        {/* Status badge */}
        <div>
          <span
            className="inline-block font-cinzel font-bold rounded-full px-4 py-1.5"
            style={{
              fontSize: "9px", letterSpacing: "0.18em", textTransform: "uppercase",
              color: cfg.badge, backgroundColor: cfg.badgeBg,
            }}
          >
            {cfg.label}
          </span>
        </div>

        {/* Heading + description */}
        <div>
          <p className="font-cinzel font-bold text-[#012E72] mb-2" style={{ fontSize: "15px" }}>
            {cfg.heading}
          </p>
          <p className="font-constantia text-gray-500 leading-relaxed" style={{ fontSize: "14px" }}>
            {cfg.body}
          </p>
        </div>

        {/* Divider */}
        <div style={{ height: "1px", background: "rgba(1,46,114,0.08)" }} />

        {/* Quote details */}
        <div className="flex flex-col gap-2.5">
          {[
            ["Insurance Types", types],
            ["Submitted",       formatDate(quote.createdAt)],
            ["Last Updated",    formatDate(quote.updatedAt)],
          ].map(([label, value]) => (
            <div key={label} className="flex gap-3">
              <span className="font-cinzel uppercase text-gray-400 flex-shrink-0"
                style={{ fontSize: "8px", letterSpacing: "0.14em", paddingTop: "3px", width: "110px" }}>
                {label}
              </span>
              <span className="font-constantia text-[#012E72]" style={{ fontSize: "13px" }}>
                {value}
              </span>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div style={{ height: "1px", background: "rgba(1,46,114,0.08)" }} />

        {/* Help note */}
        <p className="font-constantia text-gray-400 leading-relaxed" style={{ fontSize: "12px" }}>
          Questions about your quote?{" "}
          <a href="mailto:paladin.relsteam@gmail.com" className="text-[#012E72] underline underline-offset-2">
            paladin.relsteam@gmail.com
          </a>
        </p>

        {/* Reset */}
        <button
          onClick={onReset}
          className="self-start font-cinzel uppercase tracking-widest text-[#012E72] border border-[#012E72]/20 rounded-full px-5 py-2 transition-all duration-200 hover:bg-[#012E72] hover:text-white hover:border-[#012E72]"
          style={{ fontSize: "9px", letterSpacing: "0.15em" }}
        >
          Check Another Email
        </button>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function TrackQuotePage() {
  usePageTitle("Track Your Quote");
  const navigate = useNavigate();

  const [email,   setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);   // quote object
  const [error,   setError]   = useState(null);   // string

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res  = await fetch(`${process.env.REACT_APP_API_URL}/api/quotes/track`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email: email.trim() }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Something went wrong.");
      setResult(json.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
    setEmail("");
  };

  return (
    <main className="min-h-screen w-full flex flex-col">

      {/* ── Hero ── */}
      <section className="relative bg-blue-dark overflow-hidden py-20 sm:py-28">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 80% 70% at 50% 0%, rgba(0,45,181,0.4) 0%, transparent 70%),
              radial-gradient(ellipse 50% 45% at 100% 100%, rgba(0,20,90,0.35) 0%, transparent 60%)
            `,
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 mb-7">
            <div className="w-6 h-px bg-white/30" />
            <span className="font-cinzel font-bold text-white/55 border border-white/18 px-4 py-[5px] rounded-full"
              style={{ fontSize: "9px", letterSpacing: "0.28em", textTransform: "uppercase" }}>
              Client Portal
            </span>
            <div className="w-6 h-px bg-white/30" />
          </div>

          <h1
            className="font-cinzel font-black text-white tracking-[0.04em] leading-[1.1] mb-5"
            style={{ fontSize: "clamp(1.6rem, 4.5vw, 3rem)", textShadow: "0 2px 48px rgba(0,45,181,0.55)" }}
          >
            TRACK YOUR QUOTE
          </h1>

          <p
            className="font-constantia italic text-white/55 leading-[1.85] max-w-xl mx-auto"
            style={{ fontSize: "clamp(0.88rem, 1.6vw, 1.05rem)" }}
          >
            Enter the email address you used when submitting your quote request and we will show you its current status.
          </p>
        </div>
      </section>

      {/* ── Form / Result ── */}
      <section
        className="flex-1 py-16 sm:py-20 lg:py-24 px-4 sm:px-6"
        style={{ background: "linear-gradient(160deg,#EEF2F8 0%,#E8EDF6 50%,#EDF1F9 100%)" }}
      >
        <div className="max-w-lg mx-auto flex flex-col gap-8">

          {/* Search form — always visible */}
          {!result && (
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl px-8 py-8 flex flex-col gap-5"
              style={{ border: "1.5px solid rgba(1,46,114,0.10)", boxShadow: "0 4px 24px rgba(1,46,114,0.07)" }}>

              <div>
                <p className="font-cinzel font-bold text-[#012E72] mb-1" style={{ fontSize: "15px", letterSpacing: "0.04em" }}>
                  Check Your Quote Status
                </p>
                <p className="font-constantia text-gray-400" style={{ fontSize: "13px" }}>
                  We will look up the most recent quote request tied to your email.
                </p>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="font-cinzel uppercase text-[#012E72]/50"
                  style={{ fontSize: "8px", letterSpacing: "0.18em" }}>
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(null); }}
                  className="font-constantia rounded-xl px-4 py-3 outline-none transition-all duration-200"
                  style={{
                    fontSize: "14px",
                    color: "#012E72",
                    border: "1.5px solid rgba(1,46,114,0.15)",
                    backgroundColor: "#f7f9ff",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#012E72")}
                  onBlur={(e)  => (e.target.style.borderColor = "rgba(1,46,114,0.15)")}
                />
              </div>

              {error && (
                <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl"
                  style={{ backgroundColor: "#FEF3C7", border: "1.5px solid #FCD34D" }}>
                  <svg className="flex-shrink-0 mt-0.5" width="14" height="14" viewBox="0 0 24 24"
                    fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <p className="font-constantia text-amber-800" style={{ fontSize: "13px" }}>{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="inline-flex items-center justify-center gap-2 font-cinzel uppercase tracking-widest rounded-full py-3 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  fontSize: "10px", letterSpacing: "0.15em",
                  backgroundColor: "#012E72", color: "white",
                }}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin" width="13" height="13" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Looking up…
                  </>
                ) : (
                  <>Check Status <ArrowRight /></>
                )}
              </button>
            </form>
          )}

          {/* Result */}
          {result && <ResultCard quote={result} onReset={reset} />}

          {/* Bottom CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
            <button
              onClick={() => navigate("/quote")}
              className="font-cinzel uppercase tracking-widest text-[#012E72] border border-[#012E72]/20 rounded-full px-6 py-2.5 transition-all duration-200 hover:bg-[#012E72] hover:text-white"
              style={{ fontSize: "9px", letterSpacing: "0.15em" }}
            >
              Submit a New Quote
            </button>
            <button
              onClick={() => navigate("/contact")}
              className="font-cinzel uppercase tracking-widest text-[#012E72] border border-[#012E72]/20 rounded-full px-6 py-2.5 transition-all duration-200 hover:bg-[#012E72] hover:text-white"
              style={{ fontSize: "9px", letterSpacing: "0.15em" }}
            >
              Contact Our Team
            </button>
          </div>

        </div>
      </section>

      <Footer />
    </main>
  );
}
